/* =========================================================================
 * Kkumi · Wishlist Core
 * -------------------------------------------------------------------------
 * Drop-in replacement for the Swish / Wishlist King client. Provides the same
 * `window.WishlistKing` surface the existing wishlist scripts consume, but
 * backed by our own Cloudflare Worker (via the Shopify App Proxy at
 * /apps/wishlist). Must load BEFORE kkumi-wishlist*.js.
 *
 * Surface reproduced (see kkumi-wishlist.js / -grid.js / -recommendations.js):
 *   WK.config.routes.apiHost                  -> proxy prefix (/apps/wishlist)
 *   WK.config.storefront.accessToken/version  -> Storefront API (product resolve)
 *   WK._apis.wishlist.headers                 -> {} (auth is the signed proxy)
 *   WK.addWishlistItem(payload)               -> POST  /wishlists/mine/items
 *     (payload.properties: optional cart line properties — a configured
 *      bundle's choices — saved with the item and returned on every read)
 *   WK.removeWishlistItem(payload)            -> DELETE /wishlists/mine/items/:id
 *   document 'wk:countUpdated' event          -> emitted on every change
 *
 * Extras over the old client:
 *   - localStorage read-through cache: GET /wishlists/mine is served from cache
 *     within a session (stale-while-revalidate), so a browse session is ~1 call.
 *   - Guest mode: writes go to localStorage while logged out, then merge into
 *     the account once on login (POST /wishlists/mine/merge).
 *   - Fires client-side Klaviyo "Added/Removed from Wishlist" events when the
 *     onsite snippet is present.
 * ========================================================================= */
(function () {
  'use strict';
  if (window.WishlistKing) return; // never clobber an already-present client

  var cfg = window.__kkWishCfg || {};
  var API = cfg.proxy || '/apps/wishlist';
  var MINE = API + '/wishlists/mine';
  var loggedIn = !!cfg.loggedIn;
  var TTL = 5 * 60 * 1000; // cache freshness window
  var GUEST_KEY = 'kkwl:guest:v1';
  // Bumped v1 -> v2 to wipe pre-fix caches that could hold a "zombie" item
  // (saved row whose product no longer resolves on the storefront, so the
  // grid filtered it out while the bubble kept showing the stale count).
  // The old key is cleaned up below so it doesn't sit in localStorage forever.
  var CACHE_KEY = 'kkwl:cache:' + (cfg.customerId || 'anon') + ':v2';
  var LEGACY_CACHE_KEY = 'kkwl:cache:' + (cfg.customerId || 'anon') + ':v1';
  var MERGED_KEY = 'kkwl:merged:' + (cfg.customerId || '');

  // Bind the native fetch up front; all our own network calls use it directly
  // so they are never caught by the read-through interceptor below.
  var net = window.fetch ? window.fetch.bind(window) : null;

  /* ---------------- storage ---------------- */
  function readStore(key) { try { return JSON.parse(localStorage.getItem(key)); } catch (e) { return null; } }
  function writeStore(key, val) { try { localStorage.setItem(key, JSON.stringify(val)); } catch (e) {} }
  function delStore(key) { try { localStorage.removeItem(key); } catch (e) {} }

  function emptyWl() { return { id: '', publicId: '', ownerName: cfg.ownerName || null, title: null, items: [] }; }
  function guestWl() { var s = readStore(GUEST_KEY); return s && s.items ? s : emptyWl(); }
  function saveGuest(wl) { writeStore(GUEST_KEY, wl); }
  function cacheGet() { return readStore(CACHE_KEY); }            // { wl, ts }
  function cacheSet(wl) { writeStore(CACHE_KEY, { wl: wl, ts: Date.now() }); }
  // One-shot: drop the pre-v2 cache so a stuck "zombie" count from an older
  // session is wiped on first load after this ships. Safe even when absent.
  delStore(LEGACY_CACHE_KEY);
  function itemsEqual(a, b) { try { return JSON.stringify(a) === JSON.stringify(b); } catch (e) { return false; } }

  /* ---------------- events ---------------- */
  function countOf(wl) { return (wl && wl.items) ? wl.items.length : 0; }
  function currentCount() {
    if (!loggedIn) return countOf(guestWl());
    var c = cacheGet();
    return c && c.wl ? countOf(c.wl) : 0;
  }
  // The header counter (section-kkumi-navbar-clean.js) reads e.detail.count.
  // Mark our events with detail.source so the count consumers can ignore
  // copies dispatched by the embedded swish-wishlist-king @app block — it
  // keeps its own internal count that doesn't know about our worker's
  // writes, and re-fires `wk:countUpdated` from that stale state on every
  // heart click, which would flash the bubble (e.g. 0 → 1 → 0 when the user
  // removes their last item).
  function emitCount() {
    var detail = { count: currentCount(), source: 'kkwl' };
    var ev;
    try { ev = new CustomEvent('wk:countUpdated', { detail: detail, bubbles: true }); }
    catch (e) { ev = document.createEvent('CustomEvent'); ev.initCustomEvent('wk:countUpdated', true, true, detail); }
    document.dispatchEvent(ev);
  }
  function jsonResponse(obj) {
    return new Response(JSON.stringify(obj), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  /* ---------------- storefront token (served by the Worker) ----------------
   * The backend mints + returns the public Storefront API token alongside
   * wishlist reads, so the theme needs no manual token setting. A value in
   * __kkWishCfg (theme setting) still wins as an override. */
  var SFTOK_KEY = 'kkwl:sftok:v1';
  function adoptStorefront(sf) {
    if (!sf || !sf.token) return;
    if (!cfg.storefrontToken) {
      WK.config.storefront.accessToken = sf.token;
      if (sf.version) WK.config.storefront.version = sf.version;
    }
    writeStore(SFTOK_KEY, { token: sf.token, version: sf.version || null });
  }

  /* ---------------- network ---------------- */
  function netGetMine() {
    return net(MINE, { headers: {}, credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (d) {
        adoptStorefront(d && d.storefront);
        return (d && d.wishlist) ? d.wishlist : emptyWl();
      });
  }

  var revalidating = false;
  function revalidate() {
    if (revalidating) return;
    revalidating = true;
    netGetMine().then(function (wl) {
      var prev = cacheGet();
      cacheSet(wl);
      revalidating = false;
      if (!prev || !itemsEqual(prev.wl.items, wl.items)) emitCount();
    }).catch(function () { revalidating = false; });
  }

  // Resolve GET /wishlists/mine: guests from localStorage, members from the
  // cache (revalidating in the background when stale), cold-fetching otherwise.
  function serveMine() {
    if (!loggedIn) return Promise.resolve(jsonResponse({ wishlist: guestWl() }));
    var c = cacheGet();
    if (c && (Date.now() - c.ts) < TTL) return Promise.resolve(jsonResponse({ wishlist: withProps(c.wl) }));
    if (c) { revalidate(); return Promise.resolve(jsonResponse({ wishlist: withProps(c.wl) })); }
    return netGetMine()
      .then(function (wl) { cacheSet(wl); emitCount(); return jsonResponse({ wishlist: withProps(wl) }); })
      .catch(function () { return jsonResponse({ wishlist: emptyWl() }); });
  }

  /* ---------------- read-through interceptor ----------------
   * Transparently serve the members'/guests' own list from cache so the three
   * consumer scripts stay unchanged. Only the exact `GET {API}/wishlists/mine`
   * is diverted; everything else (incl. shared lists and our own writes) falls
   * straight through to the native fetch. Any hiccup => native fetch. */
  if (net) {
    // Pass a request through to the network, tapping its response for the
    // Worker-served storefront config (response object is returned untouched).
    var passTapping = function (input, init) {
      return net(input, init).then(function (res) {
        try { res.clone().json().then(function (d) { adoptStorefront(d && d.storefront); }).catch(function () {}); } catch (e) {}
        return res;
      });
    };
    window.fetch = function (input, init) {
      try {
        var url = (typeof input === 'string') ? input : (input && input.url) || '';
        var method = ((init && init.method) || (typeof input !== 'string' && input && input.method) || 'GET').toUpperCase();
        if (method === 'GET' && url.indexOf(API) !== -1 && /\/wishlists\/mine(?:[?#]|$)/.test(url)) {
          // Explicit cache-busting reads (the heart's server-verify after an
          // add/remove) must hit the network for fresh server truth — never the
          // local cache, or the verify races our in-flight write and false-fails.
          var bust = (init && (init.cache === 'no-store' || init.cache === 'reload')) || /[?&]_t=/.test(url);
          return bust ? passTapping(input, init) : serveMine();
        }
        // Shared-list reads pass through, tapped for the storefront config.
        if (method === 'GET' && url.indexOf(API + '/wishlists/') !== -1) {
          return passTapping(input, init);
        }
      } catch (e) {}
      return net(input, init);
    };
  }

  /* ---------------- saved cart-line properties ----------------
   * A configured bundle (Simple Bundles Infinite Options) is its parent variant
   * PLUS cart line properties — kit, Galaxy colour, bag colour and
   * `_bundle_selection`. Saved without them, "Move to wishlist" and back added
   * the bare parent: checkout showed the $35.95 parent with no components.
   * Items now carry `properties` to the Worker (stored per item, migration 0006)
   * and back. The Worker enforces the same bounds (normaliseProperties). */
  function cleanProps(p) {
    if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
    var out = {}, n = 0;
    Object.keys(p).forEach(function (k) {
      var v = p[k];
      if (k && k.length <= 120 && typeof v === 'string' && v.length <= 500 && n < 20) { out[k] = v; n++; }
    });
    return n ? out : null;
  }
  // Same-browser copy, so a list read from a Worker that doesn't store
  // properties yet (before its 0006 deploy), or a cache refreshed from one,
  // still hands the choices back. Server values win when present.
  var PROPS_KEY = 'kkwl:props:' + (cfg.customerId || 'anon') + ':v1';
  function rememberProps(productId, variantId, props) {
    var m = readStore(PROPS_KEY) || {};
    var k = vkey(productId, variantId);
    if (props) m[k] = props;
    else if (m[k]) delete m[k];
    else return;
    writeStore(PROPS_KEY, m);
  }
  function withProps(wl) {
    var m = readStore(PROPS_KEY);
    if (!wl || !wl.items || !m || !Object.keys(m).length) return wl;
    var out = {};
    Object.keys(wl).forEach(function (k) { out[k] = wl[k]; });
    out.items = wl.items.map(function (i) {
      if (i.properties) return i;
      var p = m[vkey(i.productId, i.variantId)];
      if (!p) return i;
      var copy = {};
      Object.keys(i).forEach(function (k) { copy[k] = i[k]; });
      copy.properties = p;
      return copy;
    });
    return out;
  }

  /* ---------------- local mutations ---------------- */
  // "productId|variantId" identity so distinct variants are independent saves
  // ('' variant = no specific variant).
  function vkey(productId, variantId) {
    var vid = (variantId == null || variantId === '') ? '' : String(variantId);
    if (!/^\d+$/.test(vid)) vid = '';
    return String(productId) + '|' + vid;
  }
  function localAdd(wl, productId, variantId, props) {
    var k = vkey(productId, variantId);
    var hit = wl.items.filter(function (i) { return vkey(i.productId, i.variantId) === k; })[0];
    if (!hit) {
      hit = { id: 'local-' + k, productId: String(productId), variantId: (variantId != null && variantId !== '') ? String(variantId) : null, createdAt: Date.now() };
      wl.items.push(hit);
    }
    // Saving the same item again with new choices updates them (one row per
    // product+variant, like the Worker).
    if (props) hit.properties = props;
    return wl;
  }
  function localRemove(wl, productId, variantId) {
    var k = vkey(productId, variantId);
    wl.items = wl.items.filter(function (i) { return vkey(i.productId, i.variantId) !== k; });
    return wl;
  }

  /* ---------------- Klaviyo (best-effort) ---------------- */
  // Attach events to the logged-in customer's profile. The onsite snippet often
  // auto-identifies Shopify customers, but we do it explicitly to be sure.
  var identified = false;
  function identifyKlaviyo() {
    if (identified || !loggedIn || !cfg.email) return;
    try {
      if (window.klaviyo && window.klaviyo.identify) { window.klaviyo.identify({ email: cfg.email }); identified = true; return; }
      if (window._learnq && window._learnq.push) { window._learnq.push(['identify', { $email: cfg.email }]); identified = true; }
    } catch (e) {}
  }
  var warnedMissing = false;
  function warnIfKlaviyoMissing() {
    if (warnedMissing || !loggedIn) return;
    if (!window.klaviyo && !window._learnq) {
      warnedMissing = true;
      try { console.warn('[kkumi] Klaviyo onsite snippet missing — wishlist events will not reach Klaviyo. Confirm the klaviyo-onsite-embed app block is enabled in the live theme.'); } catch (e) {}
    }
  }
  function fireKlaviyo(metric, payload) {
    try {
      identifyKlaviyo();
      warnIfKlaviyoMissing();
      var q = window.klaviyo || window._learnq;
      if (!q || !q.push) return;
      var handle = payload && payload.productHandle;
      q.push(['track', metric, {
        ProductID: payload && payload.productId,
        Handle: handle || undefined,
        VariantID: (payload && payload.variantId) || undefined,
        URL: handle ? (location.origin + '/products/' + handle) : undefined
      }]);
    } catch (e) {}
  }

  /* ---------------- public API ---------------- */
  function addWishlistItem(payload) {
    payload = payload || {};
    var productId = payload.productId;
    var variantId = payload.variantId || null;
    var props = cleanProps(payload.properties);
    if (productId == null) return Promise.resolve();
    fireKlaviyo('Added to Wishlist', payload);

    if (!loggedIn) {
      var g = guestWl(); localAdd(g, productId, variantId, props); saveGuest(g); emitCount();
      return Promise.resolve();
    }
    if (!net) return Promise.resolve();
    var body = { productId: String(productId), variantId: variantId != null ? String(variantId) : null };
    if (props) body.properties = props;
    return net(API + '/wishlists/mine/items', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    }).then(function (r) {
      // A non-2xx (401 login_required, 400 invalid_product, 500 server_error) is
      // a genuine failure — reject so callers can react (e.g. keep the cart line
      // on a failed "move to wishlist") instead of silently caching it as saved.
      if (!r.ok) {
        return r.json().catch(function () { return null; }).then(function (e) {
          throw new Error((e && e.error) || 'wishlist_save_failed');
        });
      }
      return r.json();
    }).then(function (res) {
      // The server returns { item } only when the row actually persisted. A full
      // list comes back 200 with { added:false, capped:true } and no item —
      // that's a failure to save, not a success, so surface it rather than
      // faking a local add.
      if (!res || !res.item) throw new Error((res && res.error) || 'wishlist_save_failed');
      if (props) rememberProps(productId, variantId, props);
      var c = cacheGet(); var wl = (c && c.wl) ? c.wl : emptyWl();
      var k = vkey(productId, variantId);
      var existing = wl.items.filter(function (i) { return vkey(i.productId, i.variantId) === k; })[0];
      var saved = res.item.properties || props;
      if (existing) {
        existing.id = res.item.id; existing.createdAt = res.item.createdAt;
        if (saved) existing.properties = saved;
      } else {
        if (saved && !res.item.properties) res.item.properties = saved;
        wl.items.push(res.item);
      }
      cacheSet(wl); emitCount();
      return res;
    });
  }

  // Switch one saved item's back-in-stock alert (PATCH /wishlists/mine/items/:id).
  // Resolves { alert } on success, rejects otherwise; the cache follows.
  function setItemAlert(itemId, on) {
    if (!loggedIn || !net || !itemId || String(itemId).indexOf('local-') === 0) return Promise.reject(new Error('unavailable'));
    return net(API + '/wishlists/mine/items/' + encodeURIComponent(itemId), {
      method: 'PATCH', credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ alert: !!on })
    }).then(function (r) {
      if (!r.ok) throw new Error('alert_' + r.status);
      var c = cacheGet();
      if (c && c.wl) { c.wl.items.forEach(function (i) { if (i.id === itemId) i.alert = !!on; }); cacheSet(c.wl); }
      return { alert: !!on };
    });
  }

  function removeWishlistItem(payload) {
    payload = payload || {};
    var productId = payload.productId;
    var passedId = payload.wishlistItemId;
    if (productId == null && passedId == null) return Promise.resolve();

    var variantId = payload.variantId;
    fireKlaviyo('Removed from Wishlist', payload);
    if (productId != null) rememberProps(productId, variantId, null);
    if (!loggedIn) {
      var g = guestWl(); if (productId != null) localRemove(g, productId, variantId); saveGuest(g); emitCount();
      return Promise.resolve();
    }
    var c = cacheGet(); var wl = (c && c.wl) ? c.wl : emptyWl();
    // Prefer the real server id from the cache (the passed one may be a stale
    // optimistic value, e.g. `true`, when an item was added without a reload).
    var rkey = productId != null ? vkey(productId, variantId) : null;
    var cached = rkey != null ? wl.items.filter(function (i) { return vkey(i.productId, i.variantId) === rkey; })[0] : null;
    if (productId != null) localRemove(wl, productId, variantId);
    cacheSet(wl); emitCount();
    if (!net) return Promise.resolve();

    function del(id) {
      if (!id || String(id).indexOf('local-') === 0 || id === true || id === 'true') return Promise.resolve();
      return net(API + '/wishlists/mine/items/' + encodeURIComponent(id), { method: 'DELETE', credentials: 'same-origin' })
        .then(function (r) { return r.json(); }).catch(function () {});
    }
    if (cached && cached.id && String(cached.id).indexOf('local-') !== 0) return del(cached.id);
    if (passedId && passedId !== true && passedId !== 'true' && String(passedId).indexOf('local-') !== 0) return del(passedId);
    // Last resort: resolve the real id from the server, then delete.
    return netGetMine().then(function (server) {
      var x = server.items.filter(function (i) { return vkey(i.productId, i.variantId) === rkey; })[0];
      return del(x && x.id);
    }).catch(function () {});
  }

  /* ---------------- guest -> account merge (once on login) ---------------- */
  function maybeMerge() {
    if (!loggedIn || !net) return;
    var g = readStore(GUEST_KEY);
    if (!g || !g.items || !g.items.length) return;
    if (localStorage.getItem(MERGED_KEY)) return;
    net(API + '/wishlists/mine/merge', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: g.items.map(function (i) {
          var it = { productId: String(i.productId), variantId: i.variantId != null ? String(i.variantId) : null };
          var p = cleanProps(i.properties);
          if (p) { it.properties = p; rememberProps(i.productId, i.variantId, p); }
          return it;
        }),
        ownerName: cfg.ownerName || undefined
      })
    }).then(function (r) { return r.json(); }).then(function (res) {
      writeStore(MERGED_KEY, 1); delStore(GUEST_KEY);
      if (res && res.wishlist) { cacheSet(res.wishlist); emitCount(); }
    }).catch(function () {});
  }

  /* ---------------- expose ---------------- */
  var WK = {
    config: {
      routes: { apiHost: API },
      storefront: { accessToken: cfg.storefrontToken || '', version: cfg.version || '2024-10' }
    },
    _apis: { wishlist: { headers: {} } },
    addWishlistItem: addWishlistItem,
    removeWishlistItem: removeWishlistItem,
    setItemAlert: setItemAlert
  };
  window.WishlistKing = WK;
  window.KkWishlist = WK;

  // Hydrate the storefront token from a previous visit so product resolution
  // works on first paint even before any wishlist fetch completes.
  adoptStorefront(readStore(SFTOK_KEY));

  // Paint the header counter from the cached count. Deferred scripts execute
  // while readyState is 'interactive' (before DOMContentLoaded fires), so wait
  // for DOMContentLoaded — by then every deferred script (incl. the navbar's
  // wk:countUpdated listener) has run. 'complete' = injected late; emit now.
  if (document.readyState === 'complete') emitCount();
  else {
    document.addEventListener('DOMContentLoaded', emitCount);
    window.addEventListener('load', emitCount); // covers the DCL-already-fired edge
  }

  maybeMerge();
})();
